import { colors, Typography, IconButton } from '@sweep-io/sweep-design';
import { Box, Slide, Stack } from '@mui/material';
import { AiAssistantConversation, AiMessage, AiMessageType } from './types';
import { ChatCompletionRequestMessageRole } from '@server/ai';
import { humanizeDate, humanizeDateVariants } from '../../helpers/humanizeDate';
import SweepAvatar from './sweepAvatar.svg?react';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { BaseDialog } from '../../common/dialogs/BaseDialog';

interface AiAssistantConversationPreviewProps {
  conversationItem: AiAssistantConversation;
  isPanelVisible: boolean;
  handleClosePanel: () => void;
  isCollapsed?: boolean;
}

const getAvatar = (message: AiMessage, item?: AiAssistantConversation) => {
  if (message?.role !== ChatCompletionRequestMessageRole.USER) {
    return (
      <Box width="24px">
        <SweepAvatar />
      </Box>
    );
  } else {
    return (
      <Box
        sx={{
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          background: colors.blue[100],
          textAlign: 'center',
        }}
      >
        <Typography variant="body-bold" color={colors.blue[500]}>
          {Array.from(item?.username?.toUpperCase() ?? '')[0]}
        </Typography>
      </Box>
    );
  }
};

export const AiAssistantConversationPreview = ({
  conversationItem,
  isPanelVisible,
  handleClosePanel,
  isCollapsed,
}: AiAssistantConversationPreviewProps) => {
  const { supportAgentInAgenticCenter } = useFeatureToggle();
  const messageLine = (message: AiMessage) => {
    if (message?.text) {
      return (
        <Box sx={{ display: 'flex', gap: 1 }} key={message?.text}>
          <Box>{getAvatar(message, conversationItem)}</Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', pag: '4px' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body-medium">
                {message?.type === AiMessageType.AI_RESPONSE ||
                message?.type === AiMessageType.AUTO_RESPONSE
                  ? 'AI Assistant'
                  : conversationItem?.username}
              </Typography>
              <Typography variant="body" color={colors.grey[500]}>
                {humanizeDate({
                  dateOrTimestamp: message?.date ?? '',
                  variant: humanizeDateVariants.TIME_WITHOUT_DATE,
                })}
              </Typography>
            </Box>

            <Typography variant="body" color={colors.grey[800]}>
              {message.text}
            </Typography>
          </Box>
        </Box>
      );
    }
  };

  const renderPanelContent = () => {
    return (
      <Stack
        maxWidth={isCollapsed ? '840px' : '410px'}
        width="100%"
        height="100%"
        gap={1.5}
        sx={{
          borderLeft: `1px solid ${colors.grey[300]}`,
          background: colors.grey[100],
          p: supportAgentInAgenticCenter ? '16px 24px' : 3,
        }}
        key={conversationItem?.id}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography variant="h5" color={isCollapsed ? colors.grey[900] : colors.grey[700]}>
              {isCollapsed
                ? `${conversationItem?.username}'s conversation with Support AI`
                : 'Preview'}
            </Typography>
            {isCollapsed && (
              <Typography variant="h5" color={colors.grey[700]}>
                {humanizeDate({
                  dateOrTimestamp: conversationItem?.updatedAt ?? '',
                  variant: humanizeDateVariants.SHORT_WITH_TIME,
                })}
              </Typography>
            )}
          </Stack>

          <IconButton variant="flat" size="small" onClick={handleClosePanel}>
            <Close />
          </IconButton>
        </Stack>

        <Stack
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'auto',
            gap: 3,
            background: colors.white,
            p: supportAgentInAgenticCenter ? 2 : 3,
            borderRadius: '4px',
            flexDirection: 'column',
            border: `1px solid ${colors.grey[300]}`,
          }}
        >
          {conversationItem?.messages?.map((el) => messageLine(el))}
        </Stack>
      </Stack>
    );
  };

  if (isCollapsed) {
    return (
      <BaseDialog
        open={isPanelVisible}
        onClose={handleClosePanel}
        PaperProps={{ sx: { pt: 0, maxHeight: '792px', height: '100%' } }}
      >
        {renderPanelContent()}
      </BaseDialog>
    );
  }

  return (
    <Slide direction="left" in={isPanelVisible} mountOnEnter unmountOnExit>
      {renderPanelContent()}
    </Slide>
  );
};
